/**
 * LifecycleTimeline — vertical, ordered timeline of dated events.
 *
 * Single responsibility: render a sequence of events with timestamps,
 * descriptive titles, and optional details. Generic over the domain;
 * the consumer maps domain entries onto `LifecycleTimelineItem`.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { FONT, GRAY } from "../design-system/tokens.js";
import { StatusBadge, type StatusTone } from "./StatusBadge.js";

export interface LifecycleTimelineItem {
  readonly id: string;
  readonly title: string;
  readonly tone: StatusTone;
  readonly timestamp: string;
  readonly summary?: string;
  readonly detail?: string;
}

export interface LifecycleTimelineProps {
  readonly items: readonly LifecycleTimelineItem[];
  /** Optional empty-state message; default omits the placeholder. */
  readonly emptyMessage?: string;
}

/**
 * Renders an ordered timeline of items. Items are displayed in array order
 * — callers are responsible for any sorting they require.
 */
export const LifecycleTimeline = React.memo(function LifecycleTimeline({
  items,
  emptyMessage,
}: LifecycleTimelineProps): React.JSX.Element {
  if (items.length === 0) {
    if (emptyMessage === undefined) {
      return <div role="list" aria-label="Lifecycle timeline" />;
    }
    return (
      <div
        role="list"
        aria-label="Lifecycle timeline"
        style={{ color: GRAY[500], fontSize: "0.875rem", fontStyle: "italic" }}
      >
        {emptyMessage}
      </div>
    );
  }

  return (
    <ol
      aria-label="Lifecycle timeline"
      style={{
        listStyle: "none",
        margin: 0,
        padding: 0,
        borderLeft: `2px solid ${GRAY[300]}`,
      }}
    >
      {items.map((item) => (
        <li
          key={item.id}
          style={{
            position: "relative",
            paddingLeft: "1.25rem",
            paddingBottom: "1rem",
          }}
        >
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              left: "-0.4375rem",
              top: "0.375rem",
              width: "0.75rem",
              height: "0.75rem",
              borderRadius: "50%",
              backgroundColor: GRAY[400],
              border: `2px solid ${GRAY[100]}`,
            }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <StatusBadge label={item.title} tone={item.tone} />
            <time
              dateTime={item.timestamp}
              style={{
                color: GRAY[500],
                fontSize: "0.75rem",
                fontFamily: FONT.mono,
              }}
            >
              {item.timestamp}
            </time>
          </div>
          {item.summary !== undefined && (
            <p
              style={{
                margin: "0.25rem 0 0",
                color: GRAY[700],
                fontSize: "0.875rem",
              }}
            >
              {item.summary}
            </p>
          )}
          {item.detail !== undefined && (
            <pre
              style={{
                margin: "0.25rem 0 0",
                padding: "0.375rem 0.5rem",
                fontFamily: FONT.mono,
                fontSize: "0.75rem",
                color: GRAY[700],
                backgroundColor: GRAY[50],
                border: `1px solid ${GRAY[300]}`,
                borderRadius: "0.25rem",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {item.detail}
            </pre>
          )}
        </li>
      ))}
    </ol>
  );
});
